import { useCallback, useRef } from 'react';

/**
 * A layer laid over the canvas (the mask brush, the Comment tool's click layer) hands every scroll
 * and pinch to the canvas, so the view pans and zooms instead of the browser zooming the whole
 * editor (Tarek, 15 Oct; 2 Nov for comments). Returns a ref for the layer.
 */
export function useWheelToCanvas<T extends HTMLElement>() {
  const detach = useRef<(() => void) | null>(null);
  return useCallback((element: T | null) => {
    detach.current?.();
    detach.current = null;
    if (!element) return;
    const onWheel = (event: WheelEvent) => {
      event.preventDefault();
      const canvas = document.querySelector<HTMLCanvasElement>('canvas.editor-canvas');
      if (!canvas) return;
      const handed = new WheelEvent('wheel', {
        deltaX: event.deltaX, deltaY: event.deltaY, deltaZ: event.deltaZ, deltaMode: event.deltaMode,
        clientX: event.clientX, clientY: event.clientY, screenX: event.screenX, screenY: event.screenY,
        ctrlKey: event.ctrlKey, metaKey: event.metaKey, shiftKey: event.shiftKey, altKey: event.altKey, bubbles: true, cancelable: true,
      });
      // Chrome answers a made event's offsetX/Y wrongly (half of clientX on a 2× screen), and the
      // canvas zooms about that point: it is set from where the canvas really is.
      const box = canvas.getBoundingClientRect();
      Object.defineProperty(handed, 'offsetX', { value: event.clientX - box.left });
      Object.defineProperty(handed, 'offsetY', { value: event.clientY - box.top });
      canvas.dispatchEvent(handed);
    };
    // Safari's pinch: kept from the page, which has no zoom of its own.
    const onGesture = (event: Event) => event.preventDefault();
    element.addEventListener('wheel', onWheel, { passive: false });
    element.addEventListener('gesturestart', onGesture);
    element.addEventListener('gesturechange', onGesture);
    detach.current = () => {
      element.removeEventListener('wheel', onWheel);
      element.removeEventListener('gesturestart', onGesture);
      element.removeEventListener('gesturechange', onGesture);
    };
  }, []);
}
